import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { getSettlementDetail } from '@/services/getSettlementDetail';
import { getDeliveryOrderContainerData } from '@/services/getDeliveryOrderData';
import { useToastNotification } from '@/hooks';
import { Proforma } from '@/types/mySettlementType';
import styles from './MySettlement.module.scss';

type DeliveryOrderDownloadButtonProps = {
  rowData: Proforma;
};

const DeliveryOrderDownloadButton = ({
  rowData,
}: DeliveryOrderDownloadButtonProps) => {
  const { showToast } = useToastNotification();
  const { t } = useTranslation('mySettlement');
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const { isPending: deliveryOrderLoading, mutate: getDeliveryOrderMutate } =
    useMutation({
      mutationKey: ['getSettlementDetail', rowData.oid],
      mutationFn: () => getSettlementDetail({ oidLiquidacion: rowData.oid }),
      onSuccess: async (settlementData) => {
        if (!settlementData || settlementData.length === 0) {
          showToast({ severity: 'error', detail: t('noContainersFound') });
          return;
        }

        const createZipAndDownload = async () => {
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();

          let hasValidFiles = false;
          let processedCount = 0;

          const containerPromises = settlementData.map(
            async (containerInfo) => {
              try {
                const cleanContainer = containerInfo.contenedor
                  .replace('/', '')
                  .replace('-', '');

                const marca = cleanContainer.slice(0, 4);
                const numero = cleanContainer.slice(4);

                const deliveryOrderData = await getDeliveryOrderContainerData({
                  marca,
                  numero,
                });

                if (deliveryOrderData) {
                  const byteCharacters = atob(
                    deliveryOrderData.replace(/-/g, '+').replace(/_/g, '/')
                  );
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i += 1) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);

                  return {
                    containerName: cleanContainer,
                    byteArray,
                  };
                }
                return null;
              } catch (error) {
                showToast({
                  severity: 'error',
                  detail: t('errorProcessingDeliveryOrders'),
                });
                return null;
              }
            }
          );

          const results = await Promise.all(containerPromises);

          results.forEach((result) => {
            if (result) {
              zip.file(`${result.containerName}.pdf`, result.byteArray);
              hasValidFiles = true;
              processedCount += 1;
            }
          });

          if (!hasValidFiles) {
            showToast({
              severity: 'error',
              detail: t('errorProcessingDeliveryOrders'),
            });
            return;
          }

          try {
            const content = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.download = `delivery-order-liquidacion-${rowData.oid}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showToast({
              severity: 'success',
              detail: t('deliveryOrdersDownloaded', { count: processedCount }),
            });
          } catch (error) {
            showToast({ severity: 'error', detail: t('errorCreatingZip') });
          }
        };

        createZipAndDownload();
      },
      onError: (error) => {
        showToast({
          severity: 'error',
          detail:
            error instanceof Error
              ? error.message
              : t('errorDownloadingDeliveryOrders'),
        });
      },
    });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (debounceTimeout) clearTimeout(debounceTimeout);

      const newTimeout = setTimeout(() => {
        getDeliveryOrderMutate();
      }, 300);

      setDebounceTimeout(newTimeout);
    },
    [debounceTimeout, getDeliveryOrderMutate]
  );

  const shouldShowButton =
    rowData.estado === 'Paga' &&
    (rowData.tipo === 'Importación' || rowData.tipo === 'VacíoImportación');

  if (!shouldShowButton) {
    return null;
  }

  return (
    <button
      type="button"
      className={`flex justify-center ${styles.resetButton}`}
      aria-label="Download Delivery Orders"
      onClick={handleClick}
      disabled={deliveryOrderLoading}
      title={t('downloadDeliveryOrders')}
    >
      <span
        className="font-bold"
        style={{
          cursor: 'pointer',
          color: deliveryOrderLoading ? '#ccc' : '#223466',
        }}
      >
        DO
      </span>
    </button>
  );
};

export default DeliveryOrderDownloadButton;
